import { useState, useMemo } from 'react'
import {
  Navbar,
  Block,
  BlockTitle,
  List,
  ListItem,
  Searchbar,
  Sheet,
  Button,
  Toolbar,
  Range,
} from 'konsta/react'
import {
  UserCog,
  Users,
  DollarSign,
  DoorOpen,
  Percent,
  Calendar,
  CheckCircle2,
  Clock,
  XCircle,
  X,
  TrendingUp,
  Shield,
  Ban,
} from 'lucide-react'
import { formatCurrency, cn } from '@/lib/utils'

// --- Types ---

type AgentStatus = 'active' | 'pending' | 'suspended'

interface Agent {
  id: string
  name: string
  telegramId: string
  rooms: number
  totalCommission: number
  rate: number
  status: AgentStatus
  since: string
  players: number
}

// --- Mock Data ---

const mockAgents: Agent[] = [
  { id: '1', name: '王大明', telegramId: '@daming_wang', rooms: 3, totalCommission: 345000, rate: 2.5, status: 'active', since: '2024-01-15', players: 47 },
  { id: '2', name: 'Sarah Lin', telegramId: '@sarah_lin', rooms: 2, totalCommission: 228000, rate: 3.0, status: 'active', since: '2024-01-10', players: 32 },
  { id: '3', name: 'Lucy Wu', telegramId: '@lucy_wu', rooms: 4, totalCommission: 567000, rate: 2.0, status: 'active', since: '2023-11-15', players: 85 },
  { id: '4', name: 'Tony Zhang', telegramId: '@tony_z', rooms: 1, totalCommission: 45000, rate: 2.5, status: 'pending', since: '2024-04-01', players: 8 },
  { id: '5', name: 'Jenny Chen', telegramId: '@jenny_c', rooms: 0, totalCommission: 89000, rate: 2.0, status: 'suspended', since: '2024-02-15', players: 0 },
]

// --- Helpers ---

function getStatusConfig(status: AgentStatus) {
  switch (status) {
    case 'active':
      return { label: '活躍', color: '#34C759', bgClass: 'bg-[#34C759]/15', Icon: CheckCircle2 }
    case 'pending':
      return { label: '待審核', color: '#FF9500', bgClass: 'bg-[#FF9500]/15', Icon: Clock }
    case 'suspended':
      return { label: '已停權', color: '#FF3B30', bgClass: 'bg-[#FF3B30]/15', Icon: XCircle }
  }
}

function StatusBadge({ status }: { status: AgentStatus }) {
  const config = getStatusConfig(status)
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium',
        config.bgClass,
      )}
      style={{ color: config.color }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full mr-1"
        style={{ backgroundColor: config.color }}
      />
      {config.label}
    </span>
  )
}

function DetailRow({
  icon: Icon,
  label,
  value,
  iconColor = '#8E8E93',
  valueColor,
}: {
  icon: React.ElementType
  label: string
  value: string
  iconColor?: string
  valueColor?: string
}) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-2.5 text-[#8E8E93]">
        <Icon className="w-4 h-4" style={{ color: iconColor }} />
        <span className="text-[14px]">{label}</span>
      </div>
      <span
        className="text-[14px] font-medium"
        style={{ color: valueColor || '#000' }}
      >
        {value}
      </span>
    </div>
  )
}

// --- Component ---

export function AdminAgentsScreen() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [adjustedRate, setAdjustedRate] = useState(2.5)

  // Filter agents
  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return mockAgents

    const q = searchQuery.toLowerCase()
    return mockAgents.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.telegramId.toLowerCase().includes(q)
    )
  }, [searchQuery])

  const handleAgentTap = (agent: Agent) => {
    setSelectedAgent(agent)
    setAdjustedRate(agent.rate)
    setSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setSheetOpen(false)
    setTimeout(() => setSelectedAgent(null), 300)
  }

  const handleApprove = () => {
    if (selectedAgent) {
      console.log(`[Admin] Approved agent: ${selectedAgent.name} (${selectedAgent.id})`)
      handleCloseSheet()
    }
  }

  const handleSuspend = () => {
    if (selectedAgent) {
      console.log(`[Admin] Suspended agent: ${selectedAgent.name} (${selectedAgent.id})`)
      handleCloseSheet()
    }
  }

  const handleActivate = () => {
    if (selectedAgent) {
      console.log(`[Admin] Activated agent: ${selectedAgent.name} (${selectedAgent.id})`)
      handleCloseSheet()
    }
  }

  const handleRateChange = () => {
    if (selectedAgent) {
      console.log(`[Admin] Updated rate for ${selectedAgent.name}: ${selectedAgent.rate}% -> ${adjustedRate}%`)
    }
  }

  // Summary stats
  const stats = useMemo(() => {
    const active = mockAgents.filter((a) => a.status === 'active').length
    const totalCommission = mockAgents.reduce((sum, a) => sum + a.totalCommission, 0)
    const totalPlayers = mockAgents.reduce((sum, a) => sum + a.players, 0)
    return { active, totalCommission, totalPlayers }
  }, [])

  return (
    <>
      <Navbar title="代理管理" />

      {/* Summary stats */}
      <Block className="!px-4 !mt-2 !mb-0">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#34C759]/15 flex items-center justify-center mx-auto mb-1">
              <UserCog className="w-4 h-4 text-[#34C759]" />
            </div>
            <p className="text-lg font-bold text-black">{stats.active}</p>
            <p className="text-[11px] text-[#8E8E93]">活躍代理</p>
          </div>
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#007AFF]/15 flex items-center justify-center mx-auto mb-1">
              <DollarSign className="w-4 h-4 text-[#007AFF]" />
            </div>
            <p className="text-sm font-bold text-black">{formatCurrency(stats.totalCommission)}</p>
            <p className="text-[11px] text-[#8E8E93]">總佣金</p>
          </div>
          <div className="bg-white rounded-2xl p-3 shadow-sm text-center">
            <div className="w-8 h-8 rounded-lg bg-[#AF52DE]/15 flex items-center justify-center mx-auto mb-1">
              <Users className="w-4 h-4 text-[#AF52DE]" />
            </div>
            <p className="text-lg font-bold text-black">{stats.totalPlayers}</p>
            <p className="text-[11px] text-[#8E8E93]">總玩家數</p>
          </div>
        </div>
      </Block>

      {/* Searchbar */}
      <div className="px-4 pt-3 pb-1 bg-[#F2F2F7]">
        <Searchbar
          placeholder="搜尋代理名稱、Telegram ID..."
          value={searchQuery}
          onInput={(e: any) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />
      </div>

      {/* Agent count */}
      <BlockTitle className="!mt-2">
        {filteredAgents.length} 位代理
      </BlockTitle>

      {/* Agent list */}
      {filteredAgents.length > 0 ? (
        <List strongIos insetIos>
          {filteredAgents.map((agent) => (
              <ListItem
                key={agent.id}
                link
                chevron={false}
                onClick={() => handleAgentTap(agent)}
                media={
                  <div className="w-10 h-10 rounded-full bg-[#007AFF]/15 flex items-center justify-center">
                    <span className="text-[14px] font-bold text-[#007AFF]">
                      {agent.name.charAt(0)}
                    </span>
                  </div>
                }
                title={
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{agent.name}</span>
                    <StatusBadge status={agent.status} />
                  </div>
                }
                subtitle={
                  <span className="text-[#8E8E93] text-[12px]">
                    {agent.telegramId} &middot; {agent.rooms} 個房間
                  </span>
                }
                after={
                  <div className="flex flex-col items-end gap-0.5">
                    <span className="text-[14px] font-semibold text-[#34C759]">
                      {formatCurrency(agent.totalCommission)}
                    </span>
                    <span className="text-[11px] text-[#8E8E93]">
                      {agent.rate}% 佣金
                    </span>
                  </div>
                }
              />
          ))}
        </List>
      ) : (
        <Block className="text-center py-12">
          <UserCog className="w-12 h-12 mx-auto text-[#C7C7CC] mb-3" />
          <p className="text-[#8E8E93] text-[15px]">
            沒有找到符合的代理
          </p>
        </Block>
      )}

      {/* Bottom padding */}
      <div className="h-8" />

      {/* Agent Detail Sheet */}
      <Sheet
        opened={sheetOpen}
        onBackdropClick={handleCloseSheet}
        className="pb-safe"
      >
        {selectedAgent && (
          <div className="flex flex-col max-h-[85vh]">
            {/* Sheet header */}
            <Toolbar top>
              <div className="flex items-center justify-between w-full px-1">
                <span className="text-[17px] font-semibold">
                  代理詳情
                </span>
                <button
                  onClick={handleCloseSheet}
                  className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-[#8E8E93]" />
                </button>
              </div>
            </Toolbar>

            {/* Sheet content */}
            <div className="overflow-y-auto flex-1 px-4 pt-2 pb-4">
              {/* Agent profile header */}
              <div className="flex items-center gap-3 py-3">
                <div className="w-14 h-14 rounded-full bg-[#007AFF]/15 flex items-center justify-center">
                  <span className="text-xl font-bold text-[#007AFF]">
                    {selectedAgent.name.charAt(0)}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-[17px] font-bold">{selectedAgent.name}</h3>
                  <p className="text-[13px] text-[#8E8E93]">{selectedAgent.telegramId}</p>
                </div>
                <StatusBadge status={selectedAgent.status} />
              </div>

              {/* Profile details */}
              <BlockTitle className="!px-0 !mt-2 !mb-2">基本資料</BlockTitle>
              <div className="bg-[#F2F2F7] rounded-xl px-4 divide-y divide-black/5">
                <DetailRow
                  icon={Calendar}
                  label="加入日期"
                  value={selectedAgent.since}
                  iconColor="#8E8E93"
                />
                <DetailRow
                  icon={DoorOpen}
                  label="管理房間"
                  value={`${selectedAgent.rooms} 個`}
                  iconColor="#007AFF"
                />
                <DetailRow
                  icon={Users}
                  label="旗下玩家"
                  value={`${selectedAgent.players} 人`}
                  iconColor="#AF52DE"
                />
              </div>

              {/* Commission details */}
              <BlockTitle className="!px-0 !mt-4 !mb-2">佣金資訊</BlockTitle>
              <div className="bg-[#F2F2F7] rounded-xl px-4 divide-y divide-black/5">
                <DetailRow
                  icon={DollarSign}
                  label="總佣金收入"
                  value={formatCurrency(selectedAgent.totalCommission)}
                  iconColor="#34C759"
                  valueColor="#34C759"
                />
                <DetailRow
                  icon={Percent}
                  label="當前佣金率"
                  value={`${selectedAgent.rate}%`}
                  iconColor="#FF9500"
                />
                <DetailRow
                  icon={TrendingUp}
                  label="平均月收入"
                  value={formatCurrency(Math.round(selectedAgent.totalCommission / 6))}
                  iconColor="#007AFF"
                />
              </div>

              {/* Commission rate adjustment */}
              <BlockTitle className="!px-0 !mt-4 !mb-2">調整佣金率</BlockTitle>
              <div className="bg-[#F2F2F7] rounded-xl px-4 py-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[14px] text-[#8E8E93]">佣金率</span>
                  <span className="text-[16px] font-bold text-[#007AFF]">
                    {adjustedRate.toFixed(1)}%
                  </span>
                </div>
                <Range
                  value={adjustedRate}
                  min={0.5}
                  max={5.0}
                  step={0.5}
                  onInput={(e: any) => setAdjustedRate(parseFloat(e.target.value))}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-[#8E8E93]">0.5%</span>
                  <span className="text-[11px] text-[#8E8E93]">5.0%</span>
                </div>
                {adjustedRate !== selectedAgent.rate && (
                  <Button
                    small
                    className="!mt-3"
                    onClick={handleRateChange}
                  >
                    儲存佣金率
                  </Button>
                )}
              </div>

              {/* Action buttons */}
              <div className="mt-4 space-y-2">
                {selectedAgent.status === 'pending' && (
                  <Button
                    large
                    onClick={handleApprove}
                  >
                    <Shield className="w-4 h-4 mr-1.5 inline" />
                    批准代理
                  </Button>
                )}

                {selectedAgent.status === 'active' && (
                  <Button
                    large
                    outline
                    className="!text-[#FF3B30] !border-[#FF3B30]"
                    onClick={handleSuspend}
                  >
                    <Ban className="w-4 h-4 mr-1.5 inline" />
                    停權代理
                  </Button>
                )}

                {selectedAgent.status === 'suspended' && (
                  <Button
                    large
                    onClick={handleActivate}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1.5 inline" />
                    重新啟用
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Sheet>
    </>
  )
}
